import { storefront } from '../libs/storefront/client'
import { gql } from '@apollo/client/core'
import { formatMoney } from '../helpers/currency-formatter'

const QUERY_SINGLE_VARIANT_RETAIL = gql`
  query getSingleVariantRetail($variantId: ID!, $country: CountryCode) @inContext(country: $country) {
    node(id: $variantId) {
      ... on ProductVariant {
        price {
          amount
        }
        compareAtPrice {
          amount
        }
        product {
          id
        }
      }
    }
  }
`

/**
 * B2B Price Custom Element
 * Displays B2B pricing with original price as compare-at
 */
export class B2BPrice extends HTMLElement {
  private variantId: string
  private quantity: number
  private cachedPriceData: {
    originalPrice: number
    productId: string
    b2bUnitPrice: number
    quantityPriceBreaks: Array<{ minimum_quantity: number; price: number }>
  } | null = null

  private moneyFormat = window.globals?.shopify?.moneyFormat || '${{amount}}'

  private formatPrice(price: number): string {
    const priceInCents = Math.round(price * 100)
    return formatMoney(priceInCents, this.moneyFormat)
  }

  constructor() {
    super()
    this.variantId = ''
    this.quantity = 1
    this.cachedPriceData = null
  }

  connectedCallback() {
    this.variantId = this.getAttribute('data-variant-id') || ''
    this.quantity = parseInt(this.getAttribute('data-quantity') || '1', 10)

    if (!this.variantId) {
      return
    }

    this.render().catch(() => {})
  }

  static get observedAttributes() {
    return ['data-quantity', 'data-variant-id']
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    if (oldValue !== newValue && this.isConnected) {
      if (name === 'data-quantity') {
        this.quantity = parseInt(newValue || '1', 10)
      } else if (name === 'data-variant-id') {
        this.variantId = newValue || ''
        this.cachedPriceData = null
      }

      if (this.variantId) {
        this.render().catch(() => {})
      }
    }
  }

  async getUnitPrices(): Promise<{ original: number; b2b: number } | null> {
    try {
      if (!this.variantId) return null

      const b2bPricing = window.globals?.customer?.b2bPricing

      if (!this.cachedPriceData) {
        const { originalPrice, productId } = await this.getVariantInfo(this.variantId)
        const b2bData = productId && b2bPricing ? b2bPricing[productId] : null

        if (!b2bData?.price) return null

        this.cachedPriceData = {
          originalPrice,
          productId,
          b2bUnitPrice: b2bData.price,
          quantityPriceBreaks: b2bData.quantity_price_breaks || []
        }
      }

      const { originalPrice, b2bUnitPrice: baseB2BPrice, quantityPriceBreaks } = this.cachedPriceData
      const b2bUnitPrice = this.calculateB2BPrice(baseB2BPrice, quantityPriceBreaks, this.quantity)

      const finalOriginalPrice = originalPrice * this.quantity
      const finalB2BPrice = b2bUnitPrice * this.quantity

      return {
        original: finalOriginalPrice,
        b2b: finalB2BPrice
      }
    } catch {
      return null
    }
  }

  private async render() {
    try {
      if (!this.variantId || this.variantId.trim() === '') {
        this.innerHTML = ''
        return
      }

      // Query for spans once at the beginning
      const originalPriceSpan = this.querySelector('[data-original-price]') as HTMLElement
      const b2bPriceSpan = this.querySelector('[data-b2b-price]') as HTMLElement
      const hasSpanStructure = originalPriceSpan && b2bPriceSpan

      // Get B2B pricing from global state
      const b2bPricing = window.globals?.customer?.b2bPricing

      // Use cached data if available and for the same variant
      if (!this.cachedPriceData || this.cachedPriceData.productId !== this.variantId) {
        const { originalPrice, productId } = await this.getVariantInfo(this.variantId)
        const b2bData = productId && b2bPricing ? b2bPricing[productId] : null

        if (!b2bData || !b2bData.price) {
          if (hasSpanStructure) {
            const finalPrice = originalPrice * this.quantity
            const formattedPrice = this.formatPrice(finalPrice)

            originalPriceSpan.textContent = ''
            originalPriceSpan.style.display = 'none'
            b2bPriceSpan.textContent = formattedPrice
            this.setAttribute('data-sale', 'false')
            this.setAttribute('data-b2b', 'false')
          }
          return
        }

        this.cachedPriceData = {
          originalPrice,
          productId,
          b2bUnitPrice: b2bData.price,
          quantityPriceBreaks: b2bData.quantity_price_breaks || []
        }
      }

      const { originalPrice, b2bUnitPrice: baseB2BPrice, quantityPriceBreaks } = this.cachedPriceData
      const b2bUnitPrice = this.calculateB2BPrice(baseB2BPrice, quantityPriceBreaks, this.quantity)

      const finalOriginalPrice = originalPrice * this.quantity
      const finalB2BPrice = b2bUnitPrice * this.quantity

      const formattedOriginalPrice = this.formatPrice(finalOriginalPrice)
      const formattedB2BPrice = this.formatPrice(finalB2BPrice)
      const isSale = originalPrice !== b2bUnitPrice

      if (hasSpanStructure) {
        // Update individual spans
        originalPriceSpan.textContent = isSale ? formattedOriginalPrice : ''
        originalPriceSpan.style.display = isSale ? '' : 'none'
        b2bPriceSpan.textContent = formattedB2BPrice
        this.setAttribute('data-sale', isSale ? 'true' : 'false')
        this.setAttribute('data-b2b', 'true')
      }
    } catch {
      if (this.cachedPriceData?.originalPrice) {
        const errorOriginalPriceSpan = this.querySelector('[data-original-price]') as HTMLElement
        const errorB2bPriceSpan = this.querySelector('[data-b2b-price]') as HTMLElement

        if (errorOriginalPriceSpan && errorB2bPriceSpan) {
          try {
            const finalPrice = this.cachedPriceData.originalPrice * this.quantity
            const formattedPrice = this.formatPrice(finalPrice)

            errorOriginalPriceSpan.textContent = ''
            errorOriginalPriceSpan.style.display = 'none'
            errorB2bPriceSpan.textContent = formattedPrice
            this.setAttribute('data-sale', 'false')
            this.setAttribute('data-b2b', 'false')
          } catch {}
        }
      }
    }
  }

  private calculateB2BPrice(
    basePrice: number,
    quantityPriceBreaks: Array<{ minimum_quantity: number; price: number }>,
    quantity: number
  ): number {
    if (!quantityPriceBreaks || quantityPriceBreaks.length === 0) {
      return basePrice
    }

    const sortedBreaks = [...quantityPriceBreaks].sort((a, b) => b.minimum_quantity - a.minimum_quantity)

    for (const priceBreak of sortedBreaks) {
      if (quantity >= priceBreak.minimum_quantity) {
        return priceBreak.price
      }
    }

    return basePrice
  }

  private async getVariantInfo(variantId: string): Promise<{ originalPrice: number; productId: string }> {
    if (!variantId || String(variantId).trim() === '') {
      throw new Error('Invalid variant ID: empty or undefined')
    }

    const country = window.globals?.localization?.country || 'AU'
    const variantIdStr = String(variantId)

    const variantGid = variantIdStr.startsWith('gid://') ? variantIdStr : `gid://shopify/ProductVariant/${variantIdStr}`

    const response = await storefront.query({
      query: QUERY_SINGLE_VARIANT_RETAIL,
      variables: {
        variantId: variantGid,
        country
      }
    })

    if (response.errors) {
      throw new Error(`GraphQL errors: ${JSON.stringify(response.errors)}`)
    }

    const variant = response.data?.node
    if (!variant) {
      throw new Error('No variant found')
    }

    const regularPrice = parseFloat(variant.price.amount)
    const compareAtPrice = variant.compareAtPrice ? parseFloat(variant.compareAtPrice.amount) : null
    const originalPrice = compareAtPrice && compareAtPrice > regularPrice ? compareAtPrice : regularPrice
    const productGid = variant.product.id
    const productId = productGid.split('/').pop()

    return { originalPrice, productId }
  }
}

/**
 * Utility function to check if a product has an actual B2B discount
 * Returns true if the B2B price is different from the original retail price
 */
export async function hasB2BDiscount(variantId: string | number): Promise<boolean> {
  try {
    if (!variantId) return false

    const isB2BCustomer = window.globals?.customer?.b2b
    const b2bPricing = window.globals?.customer?.b2bPricing

    if (!isB2BCustomer || !b2bPricing) return false

    // Get variant info to find product ID and original price
    const country = window.globals?.localization?.country || 'AU'
    const variantIdStr = String(variantId)
    const variantGid = variantIdStr.startsWith('gid://') ? variantIdStr : `gid://shopify/ProductVariant/${variantIdStr}`

    const response = await storefront.query({
      query: QUERY_SINGLE_VARIANT_RETAIL,
      variables: {
        variantId: variantGid,
        country
      }
    })

    if (response.errors || !response.data?.node) return false

    const variant = response.data.node
    const regularPrice = parseFloat(variant.price.amount)
    const compareAtPrice = variant.compareAtPrice ? parseFloat(variant.compareAtPrice.amount) : null
    const originalPrice = compareAtPrice && compareAtPrice > regularPrice ? compareAtPrice : regularPrice

    const productGid = variant.product.id
    const productId = productGid.split('/').pop()

    // Check if B2B pricing exists and is different
    const b2bData = b2bPricing[productId]
    if (!b2bData?.price) return false

    return b2bData.price !== originalPrice
  } catch {
    return false
  }
}
